// ハルシネーション検知 S1「正解データ管理」の API リクエスト関数群。
// パス: /spaces/{spaceId}/services/{serviceId}/hallucination/...（BASE_URL に /api を含むため /spaces から）。
// 応答/入力の型は @/types/api/hallucination（BE block1 design §7 契約）。

import { apiFetcher } from '@/api/api-fetcher'
import { logger } from '@/utils/logger'
import type {
  HallucinationChunksResponse,
  HallucinationExecutionStatusResponse,
  HallucinationFlagsResponse,
  HallucinationPagesResponse,
  HallucinationPaginationParams,
  HallucinationSourcesResponse,
  RecrawlSourceResponse,
  RegisterSourceRequest,
  RegisterSourceResponse,
  TogglePageResponse,
} from '@/types/api/hallucination'

/** HTTP ステータスを持つ API エラーからステータスを取り出す（403/404/409/503 の分岐用）。 */
export function getHallucinationErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: unknown }).status
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}

function basePath(spaceId: string, serviceId: string): string {
  return `/spaces/${spaceId}/services/${serviceId}/hallucination`
}

// --- 機能フラグ（Space単位） ---

/** ハルシネーション検知が当該 Space で有効か（無効でも 200 で到達可・サイドバー等の出し分け用）。 */
export async function getHallucinationFlags(
  spaceId: string,
  signal?: AbortSignal
): Promise<HallucinationFlagsResponse> {
  return apiFetcher.get<HallucinationFlagsResponse>(`/spaces/${spaceId}/hallucination/flags`, {
    signal,
  })
}

// --- 登録＋取込開始 / 再クロール ---

/** Webサイト登録＋取込開始（202）。409＝重複/同時実行超過・400＝入力不正。 */
export async function registerHallucinationSource(
  spaceId: string,
  serviceId: string,
  request: RegisterSourceRequest,
  signal?: AbortSignal
): Promise<RegisterSourceResponse> {
  const response = await apiFetcher.post<RegisterSourceResponse>(
    `${basePath(spaceId, serviceId)}/ingestion-job-executions`,
    request,
    { signal }
  )
  logger.debug('registerHallucinationSource response:', response)
  return response
}

/** 既存 Webサイトの再取得（202）。取得範囲は登録時固定。409＝進行中/同時実行超過。 */
export async function recrawlHallucinationSource(
  spaceId: string,
  serviceId: string,
  sourceId: string,
  signal?: AbortSignal
): Promise<RecrawlSourceResponse> {
  const response = await apiFetcher.post<RecrawlSourceResponse>(
    `${basePath(spaceId, serviceId)}/sources/${sourceId}/recrawl`,
    undefined,
    { signal }
  )
  logger.debug('recrawlHallucinationSource response:', response)
  return response
}

// --- 一覧（読み取り） ---

/** ソース一覧（tier1・pageSize 最大100）。 */
export async function getHallucinationSources(
  spaceId: string,
  serviceId: string,
  params?: HallucinationPaginationParams & { signal?: AbortSignal }
): Promise<HallucinationSourcesResponse> {
  const { signal, ...query } = params ?? {}
  return apiFetcher.get<HallucinationSourcesResponse>(`${basePath(spaceId, serviceId)}/sources`, {
    params: query,
    signal,
  })
}

/** ページ一覧（tier2）。 */
export async function getHallucinationPages(
  spaceId: string,
  serviceId: string,
  sourceId: string,
  params?: HallucinationPaginationParams & { signal?: AbortSignal }
): Promise<HallucinationPagesResponse> {
  const { signal, ...query } = params ?? {}
  return apiFetcher.get<HallucinationPagesResponse>(
    `${basePath(spaceId, serviceId)}/sources/${sourceId}/pages`,
    { params: query, signal }
  )
}

/** セクション（チャンク）一覧（tier3・3件ずつ・503 は ES 一時不可）。 */
export async function getHallucinationChunks(
  spaceId: string,
  serviceId: string,
  pageId: string,
  params?: HallucinationPaginationParams & { signal?: AbortSignal }
): Promise<HallucinationChunksResponse> {
  const { signal, ...query } = params ?? {}
  return apiFetcher.get<HallucinationChunksResponse>(
    `${basePath(spaceId, serviceId)}/pages/${pageId}/chunks`,
    { params: query, signal }
  )
}

/** 取込ステータス（30秒ポーリング）。 */
export async function getHallucinationExecutionStatus(
  spaceId: string,
  serviceId: string,
  executionId: string,
  signal?: AbortSignal
): Promise<HallucinationExecutionStatusResponse> {
  return apiFetcher.get<HallucinationExecutionStatusResponse>(
    `${basePath(spaceId, serviceId)}/ingestion-job-executions/${executionId}`,
    { signal }
  )
}

// --- 変更（トグル/削除） ---

/** ページ有効/無効トグル（RDB先→ES後・200 affectedChunks／409／503）。 */
export async function toggleHallucinationPage(
  spaceId: string,
  serviceId: string,
  pageId: string,
  isActive: boolean,
  signal?: AbortSignal
): Promise<TogglePageResponse> {
  return apiFetcher.patch<TogglePageResponse>(
    `${basePath(spaceId, serviceId)}/pages/${pageId}/active`,
    { isActive },
    { signal }
  )
}

/** ページ物理削除（ES先→PG後・204・冪等）。 */
export async function deleteHallucinationPage(
  spaceId: string,
  serviceId: string,
  pageId: string,
  signal?: AbortSignal
): Promise<void> {
  await apiFetcher.delete<void>(`${basePath(spaceId, serviceId)}/pages/${pageId}`, { signal })
}

/** Webサイト一括削除（DX-3・ES先→PG後・source は soft delete・204）。 */
export async function deleteHallucinationSource(
  spaceId: string,
  serviceId: string,
  sourceId: string,
  signal?: AbortSignal
): Promise<void> {
  await apiFetcher.delete<void>(`${basePath(spaceId, serviceId)}/sources/${sourceId}`, { signal })
}
